import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  Brain,
  Clock3,
  FileText,
  GitBranch,
  Layers3,
  Loader2,
  MapPin,
  Package,
  ScrollText,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react';
import {
  getV3HistoryOutline,
  v3GetChapters,
  v3GetGenerationTask,
  v3RunGenerationTask,
  v3GetSummary,
  v3StartRepairTask,
  type V3ChapterListItem,
  type V3HistoryOutlineResponse,
  type V3SummaryResponse,
} from '../lib/api';
import type { RuleObservationReport } from '../../../shared/types';

type DetailTab = 'overview' | 'volumes' | 'chapters' | 'sample' | 'rules' | 'deep';

type RulePanel = {
  key: string;
  title: string;
  headline: string;
  metrics: Array<{ label: string; value: string }>;
  bullets: string[];
};

type ObservationGroup = {
  key: string;
  title: string;
  values: string[];
};

type FactLensGroup = {
  key: string;
  title: string;
  values: string[];
};

const GOLDEN_SAMPLE_KEYS = ['chapter1', 'chapter2', 'chapter3'] as const;
const SAMPLE_PLACEHOLDER_PATTERNS = [
  /第[一二三四五六七八九十0-9]+\s*章\s*(?:正文|内容|全文)/i,
  /约\s*\d+\s*字/,
  /待(?:补充|完善|续写|扩写)/,
  /占位/,
  /后续展开/,
  /示范正文/,
];
const PROMPT_FIELD_LABELS = {
  ruleObservationExtra: '拆书观察补充',
  ruleSynthesisExtra: '规则提炼补充',
  standardOutlineExtra: '标准大纲补充',
  goldenChapterExtra: '示范正文补充',
  threeTierTotalExtra: '三层级总纲补充',
  threeTierVolumeExtra: '卷纲补充',
  threeTierChapterExtra: '章纲补充',
} as const;

function pickStrings(value: unknown, limit = 4): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).slice(0, limit);
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatWordCount(count: number) {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万字`;
  }
  return `${count}字`;
}

function formatPercent(value: unknown) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '未识别';
  }

  return `${Math.round(value <= 1 ? value * 100 : value)}%`;
}

function sanitizeSampleContent(content: string | undefined) {
  if (!content) {
    return '';
  }

  return content
    .replace(/<think>[\s\S]*?<\/think>/gi, '')
    .replace(/<(?:reasoning|thinking|analysis)>[\s\S]*?<\/(?:reasoning|thinking|analysis)>/gi, '')
    .replace(/<\/?(?:think|reasoning|thinking|analysis)>/gi, '')
    .trim();
}

function isSampleInvalid(content: string | undefined) {
  const sanitized = sanitizeSampleContent(content);
  if (!sanitized || sanitized.length < 800) {
    return true;
  }

  return SAMPLE_PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(sanitized));
}

function getTypeLabel(type: V3HistoryOutlineResponse['type']) {
  if (type === 'three-tier') return '三层级大纲';
  if (type === 'standard') return '标准拆书大纲';
  return '未知结构';
}

function getChapterPlanModeLabel(
  mode: V3HistoryOutlineResponse['chapterPlanMode'],
  inferred = false
) {
  const inferredSuffix = inferred ? '（历史记录推断）' : '';

  if (mode === 'source-book') return `按原书章节拆解${inferredSuffix}`;
  if (mode === 'target-words') return `按目标字数重规划${inferredSuffix}`;
  return '未记录';
}

function getChapterPlanSummary(params: {
  type?: V3HistoryOutlineResponse['type'];
  plannedChapterCount: number;
  sourceChapterCount?: number | null;
  chapterPlanMode?: V3HistoryOutlineResponse['chapterPlanMode'];
  chapterPlanModeInferred?: boolean;
}) {
  const inferredPrefix = params.chapterPlanModeInferred ? '根据历史记录推断，' : '';

  if (params.type !== 'three-tier') {
    return `这条记录当前保存的是本次生成的 ${params.plannedChapterCount} 章章纲。`;
  }

  if (params.chapterPlanMode === 'source-book' && typeof params.sourceChapterCount === 'number' && params.sourceChapterCount > 0) {
    return `${inferredPrefix}这次是跟着原书真实 ${params.sourceChapterCount} 章逐章拆出来的，不是重新规划的新书结构。`;
  }

  if (params.chapterPlanMode === 'target-words' && typeof params.sourceChapterCount === 'number' && params.sourceChapterCount > 0) {
    return `${inferredPrefix}这次不是还原原书 ${params.sourceChapterCount} 章，而是按目标篇幅重新规划成 ${params.plannedChapterCount} 章的新书结构。`;
  }

  if (params.chapterPlanMode === 'target-words') {
    return `${inferredPrefix}这次章数是按目标篇幅重新规划出来的，不能当成原书真实章节数。`;
  }

  if (typeof params.sourceChapterCount === 'number' && params.sourceChapterCount > 0) {
    return `原书真实有 ${params.sourceChapterCount} 章，这条历史当前展示的是 ${params.plannedChapterCount} 章的生成结果。`;
  }

  return `这条历史当前展示的是 ${params.plannedChapterCount} 章的生成结果。`;
}

function buildRuleDigest(rules: any): string {
  const rhythmPattern = rules?.rhythm?.rhythmPattern || '节奏公式待判定';
  const shuangdianTypes = pickStrings(rules?.shuangdian?.types, 2).join('、') || '多类型爽点';
  const coreConflict = rules?.conflict?.coreConflict || '核心冲突待提炼';
  const cheat = rules?.cheats?.mainCheatType || '金手指待识别';
  const romance = rules?.romance?.loveInterestConfig || '情感配置待识别';
  return `这份拆书规则更像一套可复用的连载发动机：用“${rhythmPattern}”维持推进，靠 ${shuangdianTypes} 持续刺激反馈，主线围绕 ${coreConflict} 展开，外挂配置是 ${cheat}，情感线采用 ${romance}。`;
}

function buildRulePanels(rules: any): RulePanel[] {
  const panels: RulePanel[] = [];

  if (rules?.rhythm) {
    panels.push({
      key: 'rhythm',
      title: '节奏引擎',
      headline: rules.rhythm.rhythmPattern || '章节推进节奏待识别',
      metrics: [
        { label: '平均章字数', value: typeof rules.rhythm.avgChapterLength === 'number' ? formatWordCount(rules.rhythm.avgChapterLength) : '未识别' },
        { label: '章末钩子频率', value: formatPercent(rules.rhythm.cliffhangerFrequency) },
      ],
      bullets: pickStrings([rules.rhythm.tensionBuilding, rules.rhythm.rhythmPattern], 3),
    });
  }

  if (rules?.shuangdian) {
    panels.push({
      key: 'shuangdian',
      title: '爽点配方',
      headline: rules.shuangdian.favoriteSequence || '爽点连招待识别',
      metrics: [
        { label: '爽点密度', value: formatPercent(rules.shuangdian.density) },
        { label: '主力类型', value: pickStrings(rules.shuangdian.types, 2).join(' / ') || '未识别' },
      ],
      bullets: [...pickStrings(rules.shuangdian.types, 3), ...pickStrings(rules.shuangdian.patterns, 3)].slice(0, 5),
    });
  }

  if (rules?.character) {
    panels.push({
      key: 'character',
      title: '人物模板',
      headline: rules.character.protagonistTemplate || '主角模板待识别',
      metrics: [
        { label: '主角模板', value: rules.character.protagonistTemplate || '未识别' },
        { label: '反派模板', value: rules.character.antagonistTemplate || '未识别' },
      ],
      bullets: [...pickStrings(rules.character.supportingTemplates, 3), ...pickStrings(rules.character.relationshipPatterns, 3)].slice(0, 5),
    });
  }

  if (rules?.worldview) {
    panels.push({
      key: 'worldview',
      title: '世界规则',
      headline: rules.worldview.powerSystem || '力量体系待识别',
      metrics: [
        { label: '修炼体系', value: rules.worldview.powerSystem || '未识别' },
        { label: '经济单位', value: rules.worldview.currency || '未识别' },
      ],
      bullets: [...pickStrings(rules.worldview.factions, 3), ...pickStrings(rules.worldview.worldRules, 3)].slice(0, 5),
    });
  }

  if (rules?.cheats) {
    panels.push({
      key: 'cheats',
      title: '外挂配置',
      headline: rules.cheats.mainCheatType || '金手指待识别',
      metrics: [
        { label: '表现形式', value: rules.cheats['表现形式'] || '未识别' },
        { label: '升级路线', value: rules.cheats.escalationPattern || '未识别' },
      ],
      bullets: pickStrings([rules.cheats.mainCheatType, rules.cheats['表现形式'], rules.cheats.escalationPattern], 4),
    });
  }

  if (rules?.romance) {
    panels.push({
      key: 'romance',
      title: '情感走向',
      headline: rules.romance.loveInterestConfig || '情感配置待识别',
      metrics: [
        { label: '情感配置', value: rules.romance.loveInterestConfig || '未识别' },
        { label: '推进速度', value: rules.romance.romancePace || '未识别' },
      ],
      bullets: pickStrings(rules.romance.keyRomanceEvents, 4),
    });
  }

  if (rules?.dungeon) {
    panels.push({
      key: 'dungeon',
      title: '副本地图',
      headline: rules.dungeon.mapConfig || '副本结构待识别',
      metrics: [
        { label: '地图配置', value: rules.dungeon.mapConfig || '未识别' },
        { label: '怪物设定', value: rules.dungeon.monsterSettings || '未识别' },
      ],
      bullets: pickStrings(rules.dungeon.dungeonTypes, 4),
    });
  }

  if (rules?.conflict) {
    panels.push({
      key: 'conflict',
      title: '冲突骨架',
      headline: rules.conflict.coreConflict || '核心冲突待识别',
      metrics: [
        { label: '主冲突', value: rules.conflict.coreConflict || '未识别' },
        { label: '结局类型', value: rules.conflict.endingType || '未识别' },
      ],
      bullets: pickStrings(rules.conflict.phaseConflicts, 4),
    });
  }

  if (rules?.style) {
    panels.push({
      key: 'style',
      title: '文风参数',
      headline: rules.style.languageStyle || '语言风格待识别',
      metrics: [
        { label: '叙述节奏', value: rules.style.narrativePace || '未识别' },
        { label: '描写密度', value: rules.style.descriptionDensity || '未识别' },
      ],
      bullets: pickStrings([rules.style.languageStyle, rules.style.narrativePace, rules.style.descriptionDensity], 4),
    });
  }

  if (rules?.wordCount) {
    panels.push({
      key: 'wordCount',
      title: '篇幅策略',
      headline: rules.wordCount.foreshadowingPattern || '篇幅策略待识别',
      metrics: [
        { label: '目标总字数', value: typeof rules.wordCount.targetWordCount === 'number' ? formatWordCount(rules.wordCount.targetWordCount) : '未识别' },
        { label: '目标章节数', value: typeof rules.wordCount.targetChapterCount === 'number' ? `${rules.wordCount.targetChapterCount}章` : '未识别' },
      ],
      bullets: pickStrings([rules.wordCount.foreshadowingPattern], 2),
    });
  }

  return panels;
}

function buildObservationGroups(report: RuleObservationReport | undefined): ObservationGroup[] {
  if (!report) {
    return [];
  }

  return [
    { key: 'drive', title: '核心驱动', values: pickStrings([report.baseDrive, report.payoffSequenceHint], 2) },
    { key: 'arc', title: '推进单元', values: pickStrings(report.recurringArcUnits, 4) },
    { key: 'goal', title: '章节目标', values: pickStrings(report.recurringGoalPatterns, 5) },
    { key: 'obstacle', title: '主要阻力', values: pickStrings(report.recurringObstaclePatterns, 5) },
    { key: 'outcome', title: '结果链', values: pickStrings(report.recurringOutcomePatterns, 5) },
    { key: 'action', title: '动作母题', values: pickStrings(report.actionMotifs, 5) },
    { key: 'relation', title: '关系模式', values: pickStrings(report.relationPatterns, 5) },
    { key: 'suspense', title: '悬念模式', values: pickStrings(report.suspensePatterns, 5) },
    { key: 'time', title: '时间锚点', values: pickStrings(report.timeAnchorPatterns, 5) },
    { key: 'location', title: '地点母题', values: pickStrings(report.locationPatterns, 5) },
    { key: 'item', title: '物品 / 资源', values: pickStrings(report.itemMotifs, 5) },
    { key: 'traits', title: '主角特征', values: pickStrings(report.protagonistTraits, 4) },
    { key: 'power', title: '圈层 / 权力', values: pickStrings([...report.powerSystemSignals, ...report.factionSignals], 5) },
    { key: 'resource', title: '资源信号', values: pickStrings(report.resourceSignals, 5) },
    { key: 'payoff', title: '爽点信号', values: pickStrings(report.payoffSignals, 5) },
    { key: 'romance', title: '情感信号', values: pickStrings(report.romanceSignals, 5) },
    { key: 'style', title: '文风信号', values: pickStrings(report.styleSignals, 5) },
    { key: 'stage', title: '阶段推进链', values: pickStrings(report.stageProgression, 5) },
    { key: 'scene', title: '高频场景', values: pickStrings(report.recurringSceneTypes, 5) },
  ].filter((group) => group.values.length > 0);
}

function sanitizeFactText(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function formatFactAction(action: { actor?: string; action?: string; target?: string; result?: string }) {
  const actor = sanitizeFactText(action.actor);
  const act = sanitizeFactText(action.action);
  const target = sanitizeFactText(action.target);
  const result = sanitizeFactText(action.result);
  const actionText = target
    ? `${actor || '角色'} -> ${target}${act ? `：${act}` : ''}`
    : `${actor || '角色'}${act ? `：${act}` : ''}`;

  if (!actionText.trim()) {
    return '';
  }

  return result ? `${actionText} => ${result}` : actionText;
}

function collectTopFactValues(values: string[], limit = 6) {
  const counter = new Map<string, number>();

  values
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => {
      counter.set(item, (counter.get(item) || 0) + 1);
    });

  return Array.from(counter.entries())
    .sort((left, right) => {
      if (right[1] !== left[1]) {
        return right[1] - left[1];
      }
      return left[0].localeCompare(right[0], 'zh-CN');
    })
    .slice(0, limit)
    .map(([value, count]) => (count > 1 ? `${value} ×${count}` : value));
}

function buildFactLensGroups(chapters: V3ChapterListItem[]): FactLensGroup[] {
  if (chapters.length === 0) {
    return [];
  }

  const goalValues = collectTopFactValues(chapters.map((chapter) => sanitizeFactText(chapter.facts?.chapterGoal)));
  const obstacleValues = collectTopFactValues(chapters.map((chapter) => sanitizeFactText(chapter.facts?.obstacle)));
  const outcomeValues = collectTopFactValues(chapters.map((chapter) => sanitizeFactText(chapter.facts?.outcome)));
  const actionValues = collectTopFactValues(
    chapters.flatMap((chapter) => (chapter.facts?.actions || []).map((action) => formatFactAction(action)))
  );
  const relationValues = collectTopFactValues(
    chapters.flatMap((chapter) => (chapter.facts?.importantRelations || []).map((item) => sanitizeFactText(item)))
  );
  const suspenseValues = collectTopFactValues(chapters.map((chapter) => sanitizeFactText(chapter.facts?.suspense)));
  const sceneValues = collectTopFactValues(
    chapters.flatMap((chapter) => (chapter.facts?.sceneFlow || []).map((item) => sanitizeFactText(item)))
  );
  const timeValues = collectTopFactValues(chapters.map((chapter) => sanitizeFactText(chapter.facts?.timeAnchor)));

  return [
    { key: 'goal', title: '高频目标', values: goalValues },
    { key: 'obstacle', title: '高频阻力', values: obstacleValues },
    { key: 'outcome', title: '高频结果', values: outcomeValues },
    { key: 'action', title: '动作母题', values: actionValues },
    { key: 'relation', title: '关系变化', values: relationValues },
    { key: 'suspense', title: '悬念尾钩', values: suspenseValues },
    { key: 'scene', title: '场景流转', values: sceneValues },
    { key: 'time', title: '时间锚点', values: timeValues },
  ].filter((group) => group.values.length > 0);
}

function MetricCard(props: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{props.label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{props.value}</div>
      {props.hint && <div className="mt-2 text-sm text-slate-500">{props.hint}</div>}
    </div>
  );
}

export default function HistoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [outline, setOutline] = useState<V3HistoryOutlineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [fullDeepSummary, setFullDeepSummary] = useState<V3SummaryResponse['summary'] | null>(null);
  const [deepChapters, setDeepChapters] = useState<V3ChapterListItem[]>([]);
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepLoadError, setDeepLoadError] = useState<string | null>(null);
  const [repairing, setRepairing] = useState(false);
  const [repairMessage, setRepairMessage] = useState<string | null>(null);
  const [showAllFactChapters, setShowAllFactChapters] = useState(false);
  const repairTaskRef = useRef<string | null>(null);

  const loadOutline = useCallback(async () => {
    if (!id) {
      setError('缺少历史记录 ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await getV3HistoryOutline(id);
      setOutline(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取历史大纲失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadOutline();

    return () => {
      repairTaskRef.current = null;
    };
  }, [loadOutline]);

  useEffect(() => {
    setFullDeepSummary(null);
    setDeepChapters([]);
    setDeepLoadError(null);
    setShowAllFactChapters(false);
  }, [outline?.ruleSet?.sourceBookId, outline?.deepAnalysis?.sourceBookId]);

  const goldenChapterEntries = useMemo(() => {
    if (!outline?.goldenChapters) {
      return [];
    }

    return [
      { label: '黄金第一章', content: outline.goldenChapters.chapter1 },
      { label: '黄金第二章', content: outline.goldenChapters.chapter2 },
      { label: '黄金第三章', content: outline.goldenChapters.chapter3 },
    ].filter((item): item is { label: string; content: string } => Boolean(item.content));
  }, [outline]);

  const rules = outline?.ruleSet?.rules;
  const rulePanels = useMemo(() => (rules ? buildRulePanels(rules) : []), [rules]);
  const ruleDigest = rules ? buildRuleDigest(rules) : '';
  const observationReport = rules?.observationReport;
  const observationGroups = useMemo(() => buildObservationGroups(observationReport), [observationReport]);
  const promptConfigEntries = useMemo(() => {
    const promptConfig = outline?.promptConfig;
    if (!promptConfig) {
      return [];
    }

    return (Object.entries(PROMPT_FIELD_LABELS) as Array<[keyof typeof PROMPT_FIELD_LABELS, string]>)
      .map(([key, label]) => ({
        key,
        label,
        value: typeof promptConfig[key] === 'string' ? promptConfig[key]?.trim() || '' : '',
      }))
      .filter((item) => item.value);
  }, [outline?.promptConfig]);
  const deepBookId = outline?.ruleSet?.sourceBookId || outline?.deepAnalysis?.sourceBookId || null;
  const repairableGoldenChapters = outline?.goldenChapters
    ? GOLDEN_SAMPLE_KEYS.filter((key) => isSampleInvalid(outline.goldenChapters?.[key]))
    : [];

  useEffect(() => {
    if (activeTab !== 'deep' || !deepBookId || fullDeepSummary || deepLoading) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        setDeepLoading(true);
        setDeepLoadError(null);
        const [summaryResponse, chaptersResponse] = await Promise.all([
          v3GetSummary(deepBookId),
          v3GetChapters(deepBookId),
        ]);

        if (cancelled) {
          return;
        }

        setFullDeepSummary(summaryResponse.summary);
        setDeepChapters(chaptersResponse.chapters || []);
      } catch (err) {
        if (!cancelled) {
          setDeepLoadError(err instanceof Error ? err.message : '加载完整深度分析失败');
        }
      } finally {
        if (!cancelled) {
          setDeepLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, deepBookId, deepLoading, fullDeepSummary]);

  const handleRepairGoldenChapters = useCallback(async () => {
    if (!outline?.id || repairing) {
      return;
    }

    setRepairing(true);
    setRepairMessage('正在创建历史修复任务...');
    setError(null);

    try {
      const task = await v3StartRepairTask(outline.id);
      repairTaskRef.current = task.taskId;
      v3RunGenerationTask(task.taskId);
      setRepairMessage(task.progressMessage || '修复任务已创建，正在发起执行请求...');

      const poll = async () => {
        if (repairTaskRef.current !== task.taskId) {
          return;
        }

        try {
          const taskState = await v3GetGenerationTask(task.taskId);
          if (repairTaskRef.current !== task.taskId) {
            return;
          }

          setRepairMessage(taskState.progressMessage || '正在修复示范正文...');

          if (taskState.status === 'completed') {
            repairTaskRef.current = null;
            await loadOutline();
            setRepairMessage(taskState.progressMessage || '历史示范正文修复完成');
            setRepairing(false);
            return;
          }

          if (taskState.status === 'failed') {
            repairTaskRef.current = null;
            setError(taskState.errorMessage || '历史示范正文修复失败');
            setRepairing(false);
            return;
          }
        } catch (err) {
          console.error('[HistoryDetail] Repair poll error:', err);
        }

        if (repairTaskRef.current === task.taskId) {
          setTimeout(poll, 2000);
        }
      };

      setTimeout(poll, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '启动历史修复失败');
      setRepairing(false);
      setRepairMessage(null);
    }
  }, [loadOutline, outline?.id, repairing]);

  const tabs = useMemo(() => {
    const nextTabs: Array<{
      key: DetailTab;
      label: string;
      icon: typeof BookOpen;
      description: string;
      badge?: string;
    }> = [
      {
        key: 'overview',
        label: '总览',
        icon: BookOpen,
        description: '先看这份拆书的定位、规模和来源。',
      },
    ];

    if (outline?.threeTierOutline?.volumes?.length) {
      nextTabs.push({
        key: 'volumes',
        label: '卷纲',
        icon: Layers3,
        description: '按卷查看主线推进、核心事件和卷内章节分布。',
        badge: `${outline.threeTierOutline.volumes.length}卷`,
      });
    }

    const chapterCount = outline?.threeTierOutline?.chapterOutlines?.length || outline?.standardOutline?.chapters?.length || 0;
    if (chapterCount > 0) {
      nextTabs.push({
        key: 'chapters',
        label: '章纲',
        icon: FileText,
        description: '逐章查看摘要、关键点和章节规模。',
        badge: `${chapterCount}章`,
      });
    }

    if (goldenChapterEntries.length > 0 || (outline?.type === 'standard' && outline?.ruleSetId)) {
      nextTabs.push({
        key: 'sample',
        label: '正文',
        icon: ScrollText,
        description: '查看示范正文，也可以直接补修旧历史的正文输出。',
        badge: goldenChapterEntries.length > 0 ? `${goldenChapterEntries.length}章示范` : '可修复',
      });
    }

    if (rulePanels.length > 0) {
      nextTabs.push({
        key: 'rules',
        label: '拆书维度',
        icon: Brain,
        description: '把节奏、爽点、人物、世界观等维度拆成可复用规则。',
        badge: `${rulePanels.length}维`,
      });
    }

    if (outline?.deepAnalysis) {
      nextTabs.push({
        key: 'deep',
        label: '深度分析',
        icon: Sparkles,
        description: '继续看人物、伏笔、地图和章节拆解等深层结构。',
      });
    }

    return nextTabs;
  }, [goldenChapterEntries.length, outline?.deepAnalysis, outline?.ruleSetId, outline?.standardOutline?.chapters?.length, outline?.threeTierOutline?.chapterOutlines?.length, outline?.threeTierOutline?.volumes?.length, outline?.type, rulePanels.length]);

  useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(tabs[0]?.key || 'overview');
    }
  }, [activeTab, tabs]);

  const deepAnalysis = outline?.deepAnalysis;
  const standardOutline = outline?.standardOutline;
  const threeTierOutline = outline?.threeTierOutline;
  const plannedChapterCount = outline?.plannedChapterCount || outline?.chapterCount || 0;
  const sourceChapterCount = outline?.sourceChapterCount;
  const chapterPlanModeLabel = getChapterPlanModeLabel(
    outline?.chapterPlanMode || null,
    outline?.chapterPlanModeInferred || false
  );
  const chapterPlanSummary = getChapterPlanSummary({
    type: outline?.type,
    plannedChapterCount,
    sourceChapterCount,
    chapterPlanMode: outline?.chapterPlanMode || null,
    chapterPlanModeInferred: outline?.chapterPlanModeInferred || false,
  });
  const chapterPlanSidebarSummary = `${chapterPlanSummary}${outline?.volumeCount && outline.volumeCount > 0 ? ` 当前还保存了 ${outline.volumeCount} 卷结构。` : ''}${outline?.hasGoldenChapters ? ' 并且保留了示范正文。' : ''}`;
  const deepCharacters = fullDeepSummary?.characters || deepAnalysis?.characters || [];
  const deepItems = fullDeepSummary?.items || deepAnalysis?.items || [];
  const deepLocations = fullDeepSummary?.locations || deepAnalysis?.locations || [];
  const deepForeshadowing = fullDeepSummary?.foreshadowing || deepAnalysis?.foreshadowing || {
    total: 0,
    planted: 0,
    harvested: 0,
    details: [],
  };
  const deepProtagonistArc = fullDeepSummary?.protagonistArc || deepAnalysis?.protagonistArc || [];
  const deepPlotProgress = fullDeepSummary?.plotProgress || deepAnalysis?.plotProgress || [];
  const factLensGroups = useMemo(() => buildFactLensGroups(deepChapters), [deepChapters]);
  const factCoverage = useMemo(() => {
    const total = deepChapters.length;
    const chaptersWithGoal = deepChapters.filter((chapter) => sanitizeFactText(chapter.facts?.chapterGoal)).length;
    const chaptersWithObstacle = deepChapters.filter((chapter) => sanitizeFactText(chapter.facts?.obstacle)).length;
    const chaptersWithOutcome = deepChapters.filter((chapter) => sanitizeFactText(chapter.facts?.outcome)).length;
    const chaptersWithActions = deepChapters.filter((chapter) => (chapter.facts?.actions || []).length > 0).length;
    const chaptersWithRelations = deepChapters.filter((chapter) => (chapter.facts?.importantRelations || []).length > 0).length;
    const chaptersWithSuspense = deepChapters.filter((chapter) => sanitizeFactText(chapter.facts?.suspense)).length;

    return {
      total,
      chaptersWithGoal,
      chaptersWithObstacle,
      chaptersWithOutcome,
      chaptersWithActions,
      chaptersWithRelations,
      chaptersWithSuspense,
    };
  }, [deepChapters]);
  const visibleFactChapters = useMemo(
    () => (showAllFactChapters ? deepChapters : deepChapters.slice(0, 24)),
    [deepChapters, showAllFactChapters]
  );
  const activeTabMeta = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const ActiveTabIcon = activeTabMeta?.icon;

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-slate-500" />
          <p className="text-slate-500">正在加载历史大纲...</p>
        </div>
      </div>
    );
  }

  if (error || !outline) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <div className="mb-3 flex items-center gap-3">
            <AlertCircle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">历史记录打不开</h1>
          </div>
          <p className="mb-5 text-sm">{error || '未找到对应历史记录'}</p>
          <Link
            to="/history"
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-white transition hover:bg-rose-700"
          >
            <ArrowLeft className="h-4 w-4" />
            返回历史记录
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(255,247,237,0.85),rgba(255,255,255,0.96)_30%,rgba(241,245,249,1)_100%)] px-4 py-8">
      <div className="mx-auto max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <section className="overflow-visible rounded-[32px] border border-slate-200 bg-white/90 shadow-[0_30px_80px_-50px_rgba(15,23,42,0.35)] backdrop-blur">
            <div className="border-b border-slate-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.95),rgba(239,246,255,0.92),rgba(255,255,255,0.95))] px-6 py-6 md:px-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-4xl space-y-4">
                  <Link
                    to="/history"
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-900"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    返回历史记录
                  </Link>
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-slate-300 bg-white/90 px-3 py-1 text-xs font-semibold text-slate-600">
                        {getTypeLabel(outline.type)}
                      </span>
                      {outline.hasGoldenChapters && (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                          含示范正文
                        </span>
                      )}
                      {outline.ruleSet && (
                        <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                          已关联拆书规则
                        </span>
                      )}
                      {outline.promptConfig && (
                        <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                          已使用提示词方案
                        </span>
                      )}
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">{outline.title}</h1>
                    <p className="max-w-3xl break-words text-sm leading-7 text-slate-600">{outline.synopsis || '这条历史记录没有保存简介字段。'}</p>
                  </div>
                </div>

                <div className="grid w-full gap-3 text-sm text-slate-600 md:max-w-sm md:min-w-[280px]">
                  <div className="rounded-2xl border border-white/80 bg-white/80 p-4 shadow-sm">
                    <div>题材：<span className="font-semibold text-slate-900">{outline.genre}</span></div>
                    <div className="mt-2">目标篇幅：<span className="font-semibold text-slate-900">{formatWordCount(outline.targetWordCount)}</span></div>
                    <div className="mt-2">生成时间：<span className="font-semibold text-slate-900">{formatDate(outline.createdAt)}</span></div>
                    <div className="mt-2">本次生成：<span className="font-semibold text-slate-900">{plannedChapterCount}章{outline.volumeCount > 0 ? ` / ${outline.volumeCount}卷` : ''}</span></div>
                    {typeof sourceChapterCount === 'number' && sourceChapterCount > 0 ? (
                      <div className="mt-2">原书真实章节：<span className="font-semibold text-slate-900">{sourceChapterCount}章</span></div>
                    ) : null}
                    <div className="mt-2">章数理解：<span className="font-semibold text-slate-900">{chapterPlanModeLabel}</span></div>
                    <div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50/80 px-3 py-3 text-xs leading-6 text-amber-900">
                      {chapterPlanSummary}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-6 px-4 py-6 md:px-6 xl:grid-cols-[minmax(0,1fr)_250px]">
              <div className="min-w-0 space-y-6">
                <div className="sticky top-24 z-30 rounded-[28px] border border-slate-200 bg-white/90 p-3 shadow-[0_24px_80px_-55px_rgba(15,23,42,0.45)] backdrop-blur">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">历史详情导航</div>
                      <div className="mt-1 flex items-center gap-2 text-sm text-slate-600">
                        {ActiveTabIcon ? <ActiveTabIcon className="h-4 w-4 text-slate-500" /> : null}
                        <span className="font-semibold text-slate-900">{activeTabMeta?.label}</span>
                        {activeTabMeta?.badge ? <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] text-slate-600">{activeTabMeta.badge}</span> : null}
                      </div>
                      {activeTabMeta?.description && (
                        <p className="mt-2 text-xs leading-6 text-slate-500">{activeTabMeta.description}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const active = tab.key === activeTab;
                        return (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                              active
                                ? 'bg-slate-900 text-white shadow-sm'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {tab.label}
                            {tab.badge ? (
                              <span className={`rounded-full px-2 py-0.5 text-[11px] ${active ? 'bg-white/15 text-white' : 'bg-white text-slate-500'}`}>
                                {tab.badge}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <MetricCard label="历史结构" value={getTypeLabel(outline.type)} hint="这条记录保存的是哪类生成结果" />
                    <MetricCard label="本次生成章纲" value={`${plannedChapterCount}章`} hint="这次实际生成出来并可直接查看的章纲数量" />
                    <MetricCard label="原书真实章节" value={typeof sourceChapterCount === 'number' && sourceChapterCount > 0 ? `${sourceChapterCount}章` : '未记录'} hint="上传原书识别出来的真实章节数，不等于本次生成章数" />
                    <MetricCard label="卷纲规模" value={outline.volumeCount > 0 ? `${outline.volumeCount}卷` : '未拆卷'} hint="三层级历史会额外包含卷纲" />
                    <MetricCard label="章数理解" value={chapterPlanModeLabel} hint="区分是原书逐章拆解，还是按目标篇幅重新规划的新结构" />
                  </section>

                  {outline.ruleSet && (
                    <section className="rounded-3xl border border-sky-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.95),rgba(255,255,255,0.92))] p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <Brain className="h-5 w-5 text-sky-600" />
                        <h2 className="text-xl font-semibold text-slate-900">关联拆书源</h2>
                      </div>
                      <div className="grid gap-4 md:grid-cols-3">
                        <MetricCard label="规则集 ID" value={outline.ruleSet.id} hint="生成这条历史时引用的规则库" />
                        <MetricCard label="来源小说" value={outline.ruleSet.sourceBookTitle || '未命名原书'} hint="这份大纲基于哪本书提炼出来的规则" />
                        <MetricCard label="规则样本章数" value={`${outline.ruleSet.analyzedChapters}章`} hint="用于提炼规则的样本数量" />
                      </div>
                      {deepAnalysis?.overallSummary && (
                        <p className="mt-5 rounded-2xl border border-white/80 bg-white/80 p-4 text-sm leading-7 text-slate-700">
                          {deepAnalysis.overallSummary}
                        </p>
                      )}
                    </section>
                  )}

                  {outline.promptConfig && (
                    <section className="rounded-3xl border border-violet-200 bg-[linear-gradient(135deg,rgba(245,243,255,0.96),rgba(255,255,255,0.92))] p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <Sparkles className="h-5 w-5 text-violet-600" />
                        <h2 className="text-xl font-semibold text-slate-900">本次生效提示词</h2>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <MetricCard
                          label="方案名称"
                          value={outline.promptConfig.profileName || '未命名方案'}
                          hint={outline.promptConfig.profileId ? `方案 ID：${outline.promptConfig.profileId}` : '未记录方案 ID'}
                        />
                        <MetricCard
                          label="自定义字段"
                          value={`${promptConfigEntries.length}项`}
                          hint="这次生成实际附加到模型提示词里的补充要求数量"
                        />
                        <MetricCard
                          label="结构类型"
                          value={outline.type === 'three-tier' ? '总纲 / 卷纲 / 章纲' : '标准大纲'}
                          hint="提示词会按不同生成阶段分别生效"
                        />
                        <MetricCard
                          label="是否可复用"
                          value="可复用"
                          hint="你可以回到生成页继续编辑同一套方案后再次生成"
                        />
                      </div>
                      {promptConfigEntries.length > 0 ? (
                        <div className="mt-5 grid gap-4 lg:grid-cols-2">
                          {promptConfigEntries.map((entry) => (
                            <article key={entry.key} className="rounded-2xl border border-white/90 bg-white/90 p-4 shadow-sm">
                              <div className="text-sm font-semibold text-violet-700">{entry.label}</div>
                              <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">{entry.value}</p>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-5 rounded-2xl border border-dashed border-violet-200 bg-white/80 p-4 text-sm text-slate-500">
                          这条历史记录只记录了方案名，没有额外补充字段。
                        </div>
                      )}
                    </section>
                  )}

                  <section className="grid gap-6 xl:grid-cols-[1.2fr,0.8fr]">
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-4 flex items-center gap-3">
                        <BookOpen className="h-5 w-5 text-slate-700" />
                        <h2 className="text-xl font-semibold text-slate-900">内容总览</h2>
                      </div>

                      {threeTierOutline ? (
                        <div className="space-y-4">
                          <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
                            <div className="text-sm font-semibold text-amber-700">全书总纲</div>
                            <div className="mt-2 text-lg font-semibold text-slate-900">{threeTierOutline.totalOutline.title}</div>
                            <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-slate-700">{threeTierOutline.totalOutline.totalSummary}</p>
                          </div>
                          <div className="grid gap-4 md:grid-cols-3">
                            <MetricCard label="主角设定" value={threeTierOutline.totalOutline.protagonist || '未填写'} />
                            <MetricCard label="核心矛盾" value={threeTierOutline.totalOutline.coreConflict || '未填写'} />
                            <MetricCard label="结局走向" value={threeTierOutline.totalOutline.ending || '未填写'} />
                          </div>
                        </div>
                      ) : standardOutline ? (
                        <div className="space-y-4">
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                            <div className="text-sm font-semibold text-slate-700">故事简介</div>
                            <p className="mt-3 text-sm leading-7 text-slate-700">{standardOutline.synopsis}</p>
                          </div>
                          <div className="grid gap-3">
                            {standardOutline.chapters.slice(0, 6).map((chapter) => (
                              <article key={`${chapter.index}-${chapter.title}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">第 {chapter.index} 章</span>
                                  <span className="text-base font-semibold text-slate-900">{chapter.title}</span>
                                </div>
                                <p className="mt-3 text-sm leading-7 text-slate-700">{chapter.summary}</p>
                              </article>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                          当前历史记录没有可视化结构数据。
                        </div>
                      )}
                    </div>

                    <div className="space-y-6">
                      {goldenChapterEntries.length > 0 && (
                        <section className="rounded-3xl border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.95),rgba(255,247,237,0.9))] p-6 shadow-sm">
                          <div className="mb-4 flex items-center gap-3">
                            <ScrollText className="h-5 w-5 text-amber-600" />
                            <h2 className="text-xl font-semibold text-slate-900">示范正文</h2>
                          </div>
                          <div className="space-y-3">
                            {goldenChapterEntries.map((entry) => (
                              <div key={entry.label} className="rounded-2xl border border-white/80 bg-white/90 p-4">
                                <div className="text-sm font-semibold text-amber-700">{entry.label}</div>
                                <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-7 text-slate-700 md:line-clamp-6">{entry.content}</p>
                              </div>
                            ))}
                          </div>
                        </section>
                      )}

                      {deepAnalysis && (
                        <section className="rounded-3xl border border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.95),rgba(255,255,255,0.94))] p-6 shadow-sm">
                          <div className="mb-4 flex items-center gap-3">
                            <Sparkles className="h-5 w-5 text-emerald-600" />
                            <h2 className="text-xl font-semibold text-slate-900">深度拆书快照</h2>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <MetricCard label="人物" value={`${deepAnalysis.charactersCount}`} />
                            <MetricCard label="地点" value={`${deepAnalysis.locationsCount}`} />
                            <MetricCard label="物品" value={`${deepAnalysis.itemsCount}`} />
                            <MetricCard label="伏笔" value={`${deepAnalysis.foreshadowing.total}`} />
                          </div>
                        </section>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'volumes' && threeTierOutline && (
                <div className="space-y-8">
                  <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="mb-5 flex items-center gap-3">
                      <Layers3 className="h-5 w-5 text-slate-700" />
                      <h2 className="text-xl font-semibold text-slate-900">全书总纲</h2>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <MetricCard label="书名" value={threeTierOutline.totalOutline.title} />
                      <MetricCard label="题材" value={threeTierOutline.totalOutline.genre} />
                      <MetricCard label="主角设定" value={threeTierOutline.totalOutline.protagonist} />
                      <MetricCard label="核心矛盾" value={threeTierOutline.totalOutline.coreConflict} />
                    </div>
                    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700">{threeTierOutline.totalOutline.totalSummary}</p>
                    </div>
                  </section>

                  <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="mb-5 flex items-center gap-3">
                      <GitBranch className="h-5 w-5 text-slate-700" />
                      <h2 className="text-xl font-semibold text-slate-900">卷纲</h2>
                    </div>
                    <div className="space-y-4">
                      {threeTierOutline.volumes.map((volume) => (
                        <article key={volume.number} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                          <div className="mb-3 flex flex-wrap items-center gap-3">
                            <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">第 {volume.number} 卷</span>
                            <h3 className="text-lg font-semibold text-slate-900">{volume.title}</h3>
                          </div>
                          <div className="rounded-2xl border border-white/80 bg-white p-4">
                            <div className="text-sm font-semibold text-slate-700">核心事件</div>
                            <p className="mt-2 text-sm leading-7 text-slate-700">{volume.coreEvent}</p>
                          </div>
                          <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-700">{volume.summary}</p>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {volume.chapters.map((chapter) => (
                              <span key={`${volume.number}-${chapter.index}`} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">
                                第{chapter.index}章 {chapter.title}
                              </span>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'chapters' && (
                <div className="space-y-8">
                  {threeTierOutline && (
                    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-700" />
                        <h2 className="text-xl font-semibold text-slate-900">章纲</h2>
                      </div>
                      <div className="space-y-4">
                        {threeTierOutline.chapterOutlines.map((chapter) => (
                          <article key={`${chapter.index}-${chapter.title}`} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                            <div className="mb-3 flex flex-wrap items-center gap-3">
                              <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">第 {chapter.index} 章</span>
                              <h3 className="text-lg font-semibold text-slate-900">{chapter.title}</h3>
                            </div>
                            <p className="text-sm leading-7 text-slate-700">{chapter.summary}</p>
                            {chapter.keyPoints.length > 0 && (
                              <div className="mt-4 flex flex-wrap gap-2">
                                {chapter.keyPoints.map((point) => (
                                  <span key={point} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                                    {point}
                                  </span>
                                ))}
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    </section>
                  )}

                  {standardOutline && (
                    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-700" />
                        <h2 className="text-xl font-semibold text-slate-900">章节大纲</h2>
                      </div>
                      <div className="space-y-4">
                        {standardOutline.chapters.map((chapter) => (
                          <article key={`${chapter.index}-${chapter.title}`} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                            <div className="mb-3 flex flex-wrap items-center gap-3">
                              <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">第 {chapter.index} 章</span>
                              <h3 className="text-lg font-semibold text-slate-900">{chapter.title}</h3>
                              <span className="text-sm text-slate-500">{formatWordCount(chapter.wordCount)}</span>
                            </div>
                            <p className="text-sm leading-7 text-slate-700">{chapter.summary}</p>
                            {chapter.keyPoints && chapter.keyPoints.length > 0 && (
                              <div className="mt-4 flex flex-wrap gap-2">
                                {chapter.keyPoints.map((point) => (
                                  <span key={point} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">
                                    {point}
                                  </span>
                                ))}
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}

              {activeTab === 'sample' && (
                <section className="space-y-4">
                  {repairableGoldenChapters.length > 0 && (
                    <div className="rounded-3xl border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.96),rgba(255,255,255,0.98))] p-5 shadow-sm">
                      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">旧历史示范正文可修复</h2>
                          <p className="mt-2 text-sm leading-7 text-slate-600">
                            检测到 {repairableGoldenChapters.length} 章示范正文存在占位文本、过短内容或旧版脏输出，可以直接补写回填。
                          </p>
                          {repairMessage && <p className="mt-2 text-sm text-amber-700">{repairMessage}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={handleRepairGoldenChapters}
                          disabled={repairing}
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {repairing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          {repairing ? '正在修复...' : '一键修复示范正文'}
                        </button>
                      </div>
                    </div>
                  )}

                  {goldenChapterEntries.map((entry) => (
                    <article key={entry.label} className="rounded-3xl border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.95),rgba(255,255,255,0.96))] p-6 shadow-sm">
                      <div className="mb-4 flex items-center gap-3">
                        <ScrollText className="h-5 w-5 text-amber-600" />
                        <h2 className="text-xl font-semibold text-slate-900">{entry.label}</h2>
                      </div>
                      <div className="max-h-none overflow-visible rounded-2xl border border-white/80 bg-white/90 p-5 md:max-h-[36rem] md:overflow-y-auto">
                        <p className="whitespace-pre-wrap break-words text-sm leading-8 text-slate-700">{entry.content}</p>
                      </div>
                    </article>
                  ))}
                </section>
              )}

              {activeTab === 'rules' && rules && (
                <div className="space-y-8">
                  <section className="rounded-3xl border border-slate-200 bg-[linear-gradient(135deg,rgba(239,246,255,0.95),rgba(255,255,255,0.96))] p-6 shadow-sm">
                    <div className="mb-4 flex items-center gap-3">
                      <Brain className="h-5 w-5 text-sky-600" />
                      <h2 className="text-xl font-semibold text-slate-900">拆书方法摘要</h2>
                    </div>
                    <p className="rounded-2xl border border-white/80 bg-white/90 p-5 text-sm leading-7 text-slate-700">{ruleDigest}</p>
                    <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <MetricCard label="平均章字数" value={typeof rules?.rhythm?.avgChapterLength === 'number' ? formatWordCount(rules.rhythm.avgChapterLength) : '未识别'} />
                      <MetricCard label="爽点密度" value={formatPercent(rules?.shuangdian?.density)} />
                      <MetricCard label="章末钩子频率" value={formatPercent(rules?.rhythm?.cliffhangerFrequency)} />
                      <MetricCard label="建议目标篇幅" value={typeof rules?.wordCount?.targetWordCount === 'number' ? formatWordCount(rules.wordCount.targetWordCount) : '未识别'} />
                    </div>
                  </section>

                  {observationReport && (
                    <section className="rounded-3xl border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.96),rgba(255,255,255,0.98))] p-6 shadow-sm">
                      <div className="mb-4 flex items-center gap-3">
                        <Sparkles className="h-5 w-5 text-amber-600" />
                        <h2 className="text-xl font-semibold text-slate-900">规则证据层</h2>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <MetricCard label="题材判断" value={observationReport.genreGuess || '未识别'} hint="先判断这是哪类书，再决定规则抽法" />
                        <MetricCard label="核心驱动" value={observationReport.baseDrive || '未识别'} hint="主角为什么持续往下跑" />
                        <MetricCard label="高辨识桥段" value={observationReport.standoutMoments.length > 0 ? `${observationReport.standoutMoments.length}个` : '未识别'} hint="说明这本书最有辨识度的桥段母题" />
                        <MetricCard label="章节证据" value={`${observationReport.chapterObservations.length}章`} hint="这批规则实际参考了哪些分布式样本章节" />
                      </div>

                      {observationGroups.length > 0 && (
                        <div className="mt-5 grid gap-4 xl:grid-cols-2">
                          {observationGroups.map((group) => (
                            <article key={group.key} className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
                              <div className="text-sm font-semibold text-amber-700">{group.title}</div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                {group.values.map((value) => (
                                  <span key={`${group.key}-${value}`} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                                    {value}
                                  </span>
                                ))}
                              </div>
                            </article>
                          ))}
                        </div>
                      )}

                      {observationReport.chapterObservations.length > 0 && (
                        <div className="mt-6 space-y-4">
                          <div className="text-sm font-semibold text-slate-700">抽样章节证据</div>
                          <div className="grid gap-4 xl:grid-cols-2">
                            {observationReport.chapterObservations.map((chapter) => (
                              <article key={`${chapter.chapterIndex}-${chapter.title}`} className="rounded-2xl border border-white/80 bg-white/95 p-5 shadow-sm">
                                <div className="flex flex-wrap items-center gap-3">
                                  <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">第 {chapter.chapterIndex} 章</span>
                                  <h3 className="text-base font-semibold text-slate-900">{chapter.title || '未命名章节'}</h3>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2">
                                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">阶段作用</div>
                                    <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.stageRole || '未识别'}</div>
                                  </div>
                                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">章末钩子</div>
                                    <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.hookType || '未识别'}</div>
                                  </div>
                                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">时间锚点</div>
                                    <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.timeAnchor || '未识别'}</div>
                                  </div>
                                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">核心地点</div>
                                    <div className="mt-2 text-sm font-semibold text-slate-900">{chapter.location || '未识别'}</div>
                                  </div>
                                </div>
                                <div className="mt-4 space-y-3 text-sm leading-7 text-slate-700">
                                  <p><span className="font-semibold text-slate-900">显性目标：</span>{chapter.explicitGoal || '未识别'}</p>
                                  <p><span className="font-semibold text-slate-900">主要阻力：</span>{chapter.obstacle || '未识别'}</p>
                                  <p><span className="font-semibold text-slate-900">章节结果：</span>{chapter.outcome || '未识别'}</p>
                                  <p><span className="font-semibold text-slate-900">本章回报：</span>{chapter.payoff || '未识别'}</p>
                                  <p><span className="font-semibold text-slate-900">悬念：</span>{chapter.suspense || '未识别'}</p>
                                </div>
                                <div className="mt-4 grid gap-3">
                                  {(chapter.items || []).length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {(chapter.items || []).map((item) => (
                                        <span key={`${chapter.chapterIndex}-item-${item}`} className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-3 py-1 text-xs text-fuchsia-700">
                                          物品：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {(chapter.sceneFlow || []).length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {(chapter.sceneFlow || []).map((item) => (
                                        <span key={`${chapter.chapterIndex}-scene-flow-${item}`} className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs text-cyan-700">
                                          场景流：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {(chapter.involvedRoles || []).length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {(chapter.involvedRoles || []).map((item) => (
                                        <span key={`${chapter.chapterIndex}-role-${item}`} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700">
                                          人物：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {chapter.keyActions.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {chapter.keyActions.map((item) => (
                                        <span key={`${chapter.chapterIndex}-action-${item}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                                          动作：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {chapter.relationChanges.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {chapter.relationChanges.map((item) => (
                                        <span key={`${chapter.chapterIndex}-relation-${item}`} className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs text-orange-700">
                                          关系：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {chapter.sceneTypes.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {chapter.sceneTypes.map((item) => (
                                        <span key={`${chapter.chapterIndex}-scene-${item}`} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">
                                          场景：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {chapter.powerSignals.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {chapter.powerSignals.map((item) => (
                                        <span key={`${chapter.chapterIndex}-power-${item}`} className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs text-violet-700">
                                          资源：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {chapter.romanceSignals.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {chapter.romanceSignals.map((item) => (
                                        <span key={`${chapter.chapterIndex}-romance-${item}`} className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
                                          情感：{item}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </article>
                            ))}
                          </div>
                        </div>
                      )}
                    </section>
                  )}

                  <section className="grid gap-4 xl:grid-cols-2">
                    {rulePanels.map((panel) => (
                      <article key={panel.key} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-500">{panel.title}</div>
                            <h3 className="mt-2 text-xl font-semibold text-slate-900">{panel.headline}</h3>
                          </div>
                          <div className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">{panel.key}</div>
                        </div>
                        <div className="mt-5 grid gap-3 md:grid-cols-2">
                          {panel.metrics.map((metric) => (
                            <div key={metric.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{metric.label}</div>
                              <div className="mt-2 text-base font-semibold text-slate-900">{metric.value}</div>
                            </div>
                          ))}
                        </div>
                        {panel.bullets.length > 0 && (
                          <div className="mt-5 flex flex-wrap gap-2">
                            {panel.bullets.map((bullet) => (
                              <span key={bullet} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700">
                                {bullet}
                              </span>
                            ))}
                          </div>
                        )}
                      </article>
                    ))}
                  </section>
                </div>
              )}

              {activeTab === 'deep' && deepAnalysis && (
                <div className="space-y-8">
                  <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                    <MetricCard label="分析状态" value={deepAnalysis.status} hint="后台深度拆书任务状态" />
                    <MetricCard label="已分析章节" value={`${deepAnalysis.analyzedChapters}/${deepAnalysis.totalChapters}`} />
                    <MetricCard label="人物" value={`${deepCharacters.length}`} />
                    <MetricCard label="地点" value={`${deepLocations.length}`} />
                    <MetricCard label="物品 / 伏笔" value={`${deepItems.length} / ${deepForeshadowing.total}`} />
                  </section>

                  {(deepLoading || deepLoadError || fullDeepSummary) && (
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      {deepLoading && (
                        <div className="flex items-center gap-3 text-sm text-slate-600">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          正在从深度分析表加载完整人物、地点、物品、伏笔和章节数据...
                        </div>
                      )}
                      {!deepLoading && fullDeepSummary && (
                        <p className="text-sm text-emerald-700">已切换到完整深度分析视图，下面展示的是全量数据，不再是历史快照缩略版。</p>
                      )}
                      {!deepLoading && deepLoadError && (
                        <p className="text-sm text-rose-600">完整深度分析加载失败，当前展示的是历史快照：{deepLoadError}</p>
                      )}
                    </section>
                  )}

                  {deepAnalysis.overallSummary && (
                    <section className="rounded-3xl border border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.95),rgba(255,255,255,0.96))] p-6 shadow-sm">
                      <div className="mb-4 flex items-center gap-3">
                        <Sparkles className="h-5 w-5 text-emerald-600" />
                        <h2 className="text-xl font-semibold text-slate-900">进度说明</h2>
                      </div>
                      <p className="rounded-2xl border border-white/80 bg-white/90 p-5 text-sm leading-7 text-slate-700">{deepAnalysis.overallSummary}</p>
                    </section>
                  )}

                  {deepChapters.length > 0 && (
                    <section className="rounded-3xl border border-indigo-200 bg-[linear-gradient(135deg,rgba(238,242,255,0.95),rgba(255,255,255,0.97))] p-6 shadow-sm">
                      <div className="mb-4 flex items-center gap-3">
                        <Brain className="h-5 w-5 text-indigo-600" />
                        <h2 className="text-xl font-semibold text-slate-900">章节事实层总览</h2>
                      </div>
                      <p className="text-sm leading-7 text-slate-600">
                        这里展示的是逐章 YAML 里最硬的事实链：时间锚点、目标、阻力、结果、动作链、关系变化和悬念。用户在这里能直接判断这套拆书是否真的有可复用价值。
                      </p>

                      <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <MetricCard label="目标已抽取" value={`${factCoverage.chaptersWithGoal}/${factCoverage.total}`} hint="有明确本章目标的章节数" />
                        <MetricCard label="阻力已抽取" value={`${factCoverage.chaptersWithObstacle}/${factCoverage.total}`} hint="有明确阻力的章节数" />
                        <MetricCard label="结果已抽取" value={`${factCoverage.chaptersWithOutcome}/${factCoverage.total}`} hint="有明确结果落点的章节数" />
                        <MetricCard label="动作链已抽取" value={`${factCoverage.chaptersWithActions}/${factCoverage.total}`} hint="至少有一条关键动作链的章节数" />
                        <MetricCard label="关系变化" value={`${factCoverage.chaptersWithRelations}/${factCoverage.total}`} hint="记录了关系变化的章节数" />
                        <MetricCard label="悬念尾钩" value={`${factCoverage.chaptersWithSuspense}/${factCoverage.total}`} hint="记录了章末悬念的章节数" />
                        <MetricCard label="已载入章节" value={`${deepChapters.length}章`} hint="当前历史详情已拿到的逐章结构化数据" />
                        <MetricCard label="高频模式组" value={`${factLensGroups.length}组`} hint="从逐章 facts 聚合出来的高频模式" />
                      </div>

                      {factLensGroups.length > 0 && (
                        <div className="mt-5 grid gap-4 xl:grid-cols-2">
                          {factLensGroups.map((group) => (
                            <article key={group.key} className="rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm">
                              <div className="text-sm font-semibold text-indigo-700">{group.title}</div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                {group.values.map((value) => (
                                  <span key={`${group.key}-${value}`} className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs text-indigo-700">
                                    {value}
                                  </span>
                                ))}
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  <section className="grid gap-6 xl:grid-cols-2">
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex items-center gap-3">
                        <Zap className="h-5 w-5 text-amber-600" />
                        <h2 className="text-xl font-semibold text-slate-900">主角成长轨迹</h2>
                      </div>
                      <div className="space-y-4">
                        {deepProtagonistArc.length > 0 ? deepProtagonistArc.map((arc) => (
                          <article key={`arc-${arc.chapter}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="text-sm font-semibold text-slate-900">第 {arc.chapter} 章</div>
                            <div className="mt-2 text-sm text-slate-700">境界：{arc.realm || '未记录'}</div>
                            <div className="mt-1 text-sm text-slate-700">心态：{arc.mental || '未记录'}</div>
                            {arc.gains.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {arc.gains.map((gain) => (
                                  <span key={gain} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                                    {gain}
                                  </span>
                                ))}
                              </div>
                            )}
                          </article>
                        )) : (
                          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                            这份历史没有保存主角成长轨迹。
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex items-center gap-3">
                        <Clock3 className="h-5 w-5 text-sky-600" />
                        <h2 className="text-xl font-semibold text-slate-900">主线推进</h2>
                      </div>
                      <div className="space-y-4">
                        {deepPlotProgress.length > 0 ? deepPlotProgress.map((progress) => (
                          <article key={`plot-${progress.chapter}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="text-sm font-semibold text-slate-900">第 {progress.chapter} 章</div>
                            <div className="mt-2 text-sm leading-7 text-slate-700">主线：{progress.main || '未记录'}</div>
                            {progress.romance && <div className="mt-1 text-sm leading-7 text-slate-700">感情线：{progress.romance}</div>}
                            {progress.revenge && <div className="mt-1 text-sm leading-7 text-slate-700">复仇线：{progress.revenge}</div>}
                          </article>
                        )) : (
                          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                            这份历史没有保存主线推进记录。
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="grid gap-6 xl:grid-cols-3">
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex items-center gap-3">
                        <Users className="h-5 w-5 text-rose-600" />
                        <h2 className="text-xl font-semibold text-slate-900">人物</h2>
                      </div>
                      <div className="space-y-3">
                        {deepCharacters.map((character: any) => (
                          <article key={character.name} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="font-semibold text-slate-900">{character.name}</div>
                            {(character.role || character.roleType) && <div className="mt-2 text-sm text-slate-700">角色：{character.role || character.roleType}</div>}
                            {typeof character.firstAppearance === 'number' && <div className="mt-1 text-sm text-slate-700">首次出现：第 {character.firstAppearance} 章</div>}
                            {character.relationshipToProtagonist && <div className="mt-1 text-sm text-slate-700">与主角关系：{character.relationshipToProtagonist}</div>}
                            {character.change && <div className="mt-2 text-sm leading-7 text-slate-700">变化：{character.change}</div>}
                            {character.description && <div className="mt-2 text-sm leading-7 text-slate-700">{character.description}</div>}
                          </article>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex items-center gap-3">
                        <Package className="h-5 w-5 text-violet-600" />
                        <h2 className="text-xl font-semibold text-slate-900">物品</h2>
                      </div>
                      <div className="space-y-3">
                        {deepItems.map((item: any) => (
                          <article key={item.name} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="font-semibold text-slate-900">{item.name}</div>
                            <div className="mt-2 text-sm text-slate-700">类型：{item.type || '未记录'}</div>
                            <div className="mt-1 text-sm text-slate-700">稀有度：{item.rarity || '未记录'}</div>
                            {(item.owner || item.currentOwner) && <div className="mt-1 text-sm text-slate-700">持有者：{item.owner || item.currentOwner}</div>}
                            {typeof item.chapterAppeared === 'number' && <div className="mt-1 text-sm text-slate-700">首次出现：第 {item.chapterAppeared} 章</div>}
                            {Array.isArray(item.abilities) && item.abilities.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {item.abilities.map((ability: string) => (
                                  <span key={ability} className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs text-violet-700">
                                    {ability}
                                  </span>
                                ))}
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex items-center gap-3">
                        <MapPin className="h-5 w-5 text-cyan-600" />
                        <h2 className="text-xl font-semibold text-slate-900">地点</h2>
                      </div>
                      <div className="space-y-3">
                        {deepLocations.map((location: any) => (
                          <article key={location.name} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="font-semibold text-slate-900">{location.name}</div>
                            <div className="mt-2 text-sm text-slate-700">类型：{location.type || '未记录'}</div>
                            {typeof location.chapterUnlocked === 'number' && <div className="mt-1 text-sm text-slate-700">首次出现：第 {location.chapterUnlocked} 章</div>}
                            {location.importance && <div className="mt-1 text-sm text-slate-700">重要度：{location.importance}</div>}
                            {location.isDungeon && <div className="mt-1 text-sm text-cyan-700">副本 / 地图节点</div>}
                            {location.description && <div className="mt-2 text-sm leading-7 text-slate-700">{location.description}</div>}
                          </article>
                        ))}
                      </div>
                    </div>
                  </section>

                  <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="mb-5 flex items-center gap-3">
                      <GitBranch className="h-5 w-5 text-slate-700" />
                      <h2 className="text-xl font-semibold text-slate-900">伏笔</h2>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <MetricCard label="总数" value={`${deepForeshadowing.total}`} />
                      <MetricCard label="已埋" value={`${deepForeshadowing.planted}`} />
                      <MetricCard label="已回收" value={`${deepForeshadowing.harvested}`} />
                    </div>
                    <div className="mt-5 space-y-3">
                      {deepForeshadowing.details.map((detail: any, index: number) => (
                        <article key={`${detail.content}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              detail.status === 'harvested'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {detail.status === 'harvested' ? '已回收' : '已埋设'}
                            </span>
                            {typeof detail.chapterPlanted === 'number' && <span className="text-xs text-slate-500">埋设于第 {detail.chapterPlanted} 章</span>}
                            {typeof detail.chapterHarvested === 'number' && <span className="text-xs text-slate-500">回收于第 {detail.chapterHarvested} 章</span>}
                          </div>
                          <p className="mt-3 text-sm leading-7 text-slate-700">{detail.content}</p>
                        </article>
                      ))}
                    </div>
                  </section>

                  {deepChapters.length > 0 && (
                    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                      <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-slate-700" />
                          <div>
                            <h2 className="text-xl font-semibold text-slate-900">章节事实时间线</h2>
                            <p className="mt-1 text-sm leading-6 text-slate-500">
                              逐章查看时间锚点、目标、阻力、结果、动作链和关系变化。默认先展示前 24 章，避免长篇历史一次性铺满页面。
                            </p>
                          </div>
                        </div>
                        {deepChapters.length > 24 && (
                          <button
                            type="button"
                            onClick={() => setShowAllFactChapters((current) => !current)}
                            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
                          >
                            {showAllFactChapters ? '收起章节事实' : `展开全部 ${deepChapters.length} 章`}
                          </button>
                        )}
                      </div>
                      <div className="space-y-4">
                        {visibleFactChapters.map((chapter) => (
                          <article key={`chapter-${chapter.chapter}`} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                            <div className="mb-3 flex flex-wrap items-center gap-3">
                              <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">第 {chapter.chapter} 章</span>
                              <h3 className="text-lg font-semibold text-slate-900">{chapter.title}</h3>
                              {chapter.facts?.timeAnchor && (
                                <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs text-indigo-700">
                                  {chapter.facts.timeAnchor}
                                </span>
                              )}
                              {chapter.location?.current && (
                                <span className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs text-cyan-700">
                                  {chapter.location.current}
                                </span>
                              )}
                            </div>

                            <p className="text-sm leading-7 text-slate-700">{chapter.summary}</p>

                            <div className="mt-4 flex flex-wrap gap-2">
                              {(chapter.chapterTypes || []).map((tag) => (
                                <span key={tag} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">
                                  {tag}
                                </span>
                              ))}
                              {(chapter.protagonistGains || []).map((gain) => (
                                <span key={`${chapter.chapter}-gain-${gain}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                                  收获：{gain}
                                </span>
                              ))}
                            </div>

                            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">目标</div>
                                <div className="mt-2 font-semibold text-slate-900">{chapter.facts?.chapterGoal || chapter.protagonistGoal || '未记录'}</div>
                              </div>
                              <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">阻力</div>
                                <div className="mt-2 font-semibold text-slate-900">{chapter.facts?.obstacle || '未记录'}</div>
                              </div>
                              <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">结果</div>
                                <div className="mt-2 font-semibold text-slate-900">{chapter.facts?.outcome || '未记录'}</div>
                              </div>
                              <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-400">悬念 / 钩子</div>
                                <div className="mt-2 font-semibold text-slate-900">{chapter.facts?.suspense || chapter.cliffhanger || '未记录'}</div>
                              </div>
                            </div>

                            {(chapter.facts?.sceneFlow?.length || chapter.facts?.actions?.length || chapter.facts?.importantRelations?.length || chapter.plotProgress?.main) && (
                              <div className="mt-4 space-y-3">
                                {chapter.plotProgress?.main && (
                                  <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                    <span className="font-semibold text-slate-900">主线推进：</span>
                                    {chapter.plotProgress.main}
                                  </div>
                                )}
                                {chapter.facts?.sceneFlow?.length ? (
                                  <div className="rounded-2xl border border-white/80 bg-white p-4">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">场景流转</div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {chapter.facts.sceneFlow.map((item) => (
                                        <span key={`${chapter.chapter}-scene-${item}`} className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs text-cyan-700">
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                                {chapter.facts?.actions?.length ? (
                                  <div className="rounded-2xl border border-white/80 bg-white p-4">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">动作链</div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {chapter.facts.actions.map((action, index) => {
                                        const label = formatFactAction(action);
                                        if (!label) {
                                          return null;
                                        }
                                        return (
                                          <span key={`${chapter.chapter}-action-${index}-${label}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                                            {label}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : null}
                                {chapter.facts?.importantRelations?.length ? (
                                  <div className="rounded-2xl border border-white/80 bg-white p-4">
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">关系变化</div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {chapter.facts.importantRelations.map((item) => (
                                        <span key={`${chapter.chapter}-relation-${item}`} className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs text-orange-700">
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            )}

                            <div className="mt-4 grid gap-3 md:grid-cols-3">
                              <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                主角：{chapter.protagonist || '未记录'}
                              </div>
                              <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                境界：{chapter.realm || '未记录'}
                              </div>
                              <div className="rounded-2xl border border-white/80 bg-white p-4 text-sm text-slate-700">
                                物品数：{chapter.items?.length || 0}
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                      {deepChapters.length > 24 && !showAllFactChapters && (
                        <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500">
                          当前先展示前 24 章，点击上方按钮可以展开全部章节事实时间线。
                        </div>
                      )}
                    </section>
                  )}
                </div>
              )}
                </div>
              </div>

              <aside className="hidden xl:block">
                <div className="sticky top-44 space-y-4">
                  <div className="rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-[0_20px_70px_-50px_rgba(15,23,42,0.45)] backdrop-blur">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">快速切换</div>
                    <div className="mt-4 space-y-2">
                      {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const active = tab.key === activeTab;
                        return (
                          <button
                            key={`aside-${tab.key}`}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex w-full items-start gap-3 rounded-2xl px-4 py-3 text-left transition ${
                              active
                                ? 'bg-slate-900 text-white shadow-sm'
                                : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${active ? 'text-white' : 'text-slate-400'}`} />
                            <span className="min-w-0">
                              <span className="flex items-center gap-2 text-sm font-semibold">
                                {tab.label}
                                {tab.badge ? (
                                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${active ? 'bg-white/15 text-white' : 'bg-white text-slate-500'}`}>
                                    {tab.badge}
                                  </span>
                                ) : null}
                              </span>
                              <span className={`mt-1 block text-xs leading-5 ${active ? 'text-slate-200' : 'text-slate-500'}`}>
                                {tab.description}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="rounded-[28px] border border-slate-200 bg-[linear-gradient(145deg,rgba(255,251,235,0.92),rgba(255,255,255,0.96))] p-5 shadow-[0_20px_70px_-50px_rgba(15,23,42,0.45)]">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-500">当前焦点</div>
                    <div className="mt-3 text-lg font-semibold text-slate-900">{activeTabMeta?.label}</div>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{activeTabMeta?.description}</p>
                    <div className="mt-4 rounded-2xl border border-white/80 bg-white/90 px-4 py-3 text-sm text-slate-600">
                      {chapterPlanSidebarSummary}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
